import { Card, CardContent } from "@aurumiq/ui/components/card";
import { Input } from "@aurumiq/ui/components/input";
import {
  type ColumnDef,
  createColumnHelper,
  DataTable,
  type PaginationState,
} from "@aurumiq/ui/composite/data-table";
import { StatusTag, statusVariant } from "@aurumiq/ui/composite/status-tag";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router";
import { PageHeader } from "@/app/page-header";
import { errorMessage, qs, rawApi } from "@/lib/api";
import type { OrgSummary, Paginated } from "@/lib/types";

const col = createColumnHelper<OrgSummary>();

/** Org list expanded with seat usage; links into the org's Licenses / Devices tab. */
export function OrgSeatTable({
  tab,
  title,
  description,
}: {
  tab: "licenses" | "devices";
  title: string;
  description: string;
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });
  const orgs = useQuery({
    queryKey: ["admin", "orgs", { q, ...pagination }],
    placeholderData: keepPreviousData,
    queryFn: () =>
      rawApi.get<Paginated<OrgSummary>>(
        `/api/v1/admin/orgs${qs({ page: pagination.pageIndex + 1, page_size: pagination.pageSize, q })}`,
      ),
  });
  const columns = [
    col.accessor("name", {
      header: "Organisation",
      cell: (c) => (
        <Link
          to={`/orgs/${c.row.original.id}/${tab}`}
          className="font-semibold text-primary hover:underline"
        >
          {c.getValue()}
        </Link>
      ),
    }),
    col.accessor("code", {
      header: "Code",
      cell: (c) => <span className="font-mono">{c.getValue()}</span>,
    }),
    col.accessor("status", {
      header: "Status",
      cell: (c) => (
        <StatusTag variant={statusVariant(c.getValue())}>{c.getValue().toUpperCase()}</StatusTag>
      ),
    }),
    col.accessor("plan_code", { header: "Plan" }),
    col.accessor((r) => r.seats_used, {
      id: "seats",
      header: "Seats used",
      meta: { numeric: true },
      cell: (c) => {
        const r = c.row.original;
        const full = r.seats > 0 && r.seats_used >= r.seats;
        return (
          <span className={full ? "font-semibold text-warning" : undefined}>
            {r.seats_used} / {r.seats}
          </span>
        );
      },
    }),
  ] as ColumnDef<OrgSummary, unknown>[];
  const total = orgs.data?.total ?? 0;
  return (
    <>
      <PageHeader title={title} description={description} />
      <DataTable
        columns={columns}
        data={orgs.data?.items ?? []}
        isLoading={orgs.isPending}
        pageCount={Math.max(1, Math.ceil(total / pagination.pageSize))}
        pagination={pagination}
        onPaginationChange={setPagination}
        onRowClick={(row) => navigate(`/orgs/${row.original.id}/${tab}`)}
        emptyMessage={
          orgs.isError
            ? `Could not load organisations: ${errorMessage(orgs.error)}`
            : "No organisations."
        }
        toolbar={
          <Input
            placeholder="Search organisation name or code…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
            className="max-w-xs"
          />
        }
      />
    </>
  );
}

export function LicensesPage() {
  return (
    <OrgSeatTable
      tab="licenses"
      title="Licenses"
      description="Licences are issued per organisation. Pick an organisation to see its licences and seat usage."
    />
  );
}

/** `/licenses/:id?org_id=…` → the owning org's Licenses tab; without org_id we cannot resolve it yet. */
export function LicenseRedirectPage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const orgId = sp.get("org_id") ?? sp.get("org");
  if (orgId) return <Navigate to={`/orgs/${orgId}/licenses`} replace />;
  return (
    <>
      <PageHeader title="License" description={id ? `Licence ${id}` : undefined} />
      <Card>
        <CardContent className="text-muted-foreground text-sm">
          There is no global licence lookup yet. Open the licence from its organisation: go to{" "}
          <Link to="/licenses" className="text-primary underline">
            Licenses
          </Link>
          , pick the organisation, then the Licenses tab.
        </CardContent>
      </Card>
    </>
  );
}
